import {
  computeMaxTagCountForRows,
  measureRenderedOrMirrorTagWidths,
} from 'utils/compute-max-tag-count';
import { RefObject, useLayoutEffect, useRef, useState } from 'react';

const getSelectRoot = (holder: unknown): HTMLElement | null => {
  if (!holder || typeof holder !== 'object') return null;

  const withNative = holder as { nativeElement?: HTMLElement };
  const native =
    withNative.nativeElement && withNative.nativeElement.nodeType === 1
      ? withNative.nativeElement
      : (holder as HTMLElement);

  if (!native || typeof native.querySelector !== 'function') return null;

  if (native.classList?.contains('ant-select')) return native;
  return native.querySelector('.ant-select');
};

/**
 * Caps Ant Select `maxTagCount` so tags fit in `maxRows` (default 2), with a
 * "+N" rest chip when there is overflow — same idea as `maxTagCount="responsive"`
 * but for more than one line.
 */
export const useMultiLineMaxTagCount = (
  selectRef: RefObject<unknown>,
  labels: string[],
  maxRows = 2
): number => {
  const [maxTagCount, setMaxTagCount] = useState(labels.length);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const labelsKey = labels.join('\0');

  useLayoutEffect(() => {
    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    let timerEarly = 0;
    let timerLate = 0;
    let observer: ResizeObserver | null = null;

    const update = () => {
      if (cancelled) return;

      const root = getSelectRoot(selectRef.current);
      const currentLabels = labelsRef.current;
      if (!root) {
        setMaxTagCount(currentLabels.length);
        return;
      }

      const selector = root.querySelector(
        '.ant-select-selector'
      ) as HTMLElement | null;
      if (!selector) {
        setMaxTagCount(currentLabels.length);
        return;
      }

      // Width can be 0 while the drawer is still mounting/animating.
      if (selector.clientWidth <= 0) return;

      const style = getComputedStyle(selector);
      const horizontalPadding =
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0);
      const arrow = root.querySelector(
        '.ant-select-arrow'
      ) as HTMLElement | null;
      const arrowWidth = arrow ? arrow.offsetWidth + 8 : 24;
      // Keep room for the multiple-select search input suffix.
      const searchWidth = 32;
      const availableWidth = Math.max(
        0,
        selector.clientWidth - horizontalPadding - arrowWidth - searchWidth
      );

      const tagWidths = measureRenderedOrMirrorTagWidths(
        currentLabels,
        selector
      );

      const next = computeMaxTagCountForRows(tagWidths, availableWidth, {
        maxRows,
        restWidth: 52,
      });

      setMaxTagCount((prev) => (prev === next ? prev : next));
    };

    update();

    // First pass often runs before tags/drawer width settle. Remeasure after
    // paint (and once more shortly after) so we don't leave clipped tags
    // without a "+N" until the user opens the dropdown.
    raf1 = requestAnimationFrame(() => {
      update();
      raf2 = requestAnimationFrame(update);
    });
    timerEarly = window.setTimeout(update, 50);
    // Drawer open animation can finish after the first layout pass.
    timerLate = window.setTimeout(update, 300);

    const root = getSelectRoot(selectRef.current);
    if (root) {
      observer = new ResizeObserver(update);
      observer.observe(root);
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(timerEarly);
      window.clearTimeout(timerLate);
      observer?.disconnect();
    };
  }, [selectRef, labelsKey, maxRows]);

  return maxTagCount;
};
